/**
 * CDD / Incorporation DOC 콤보박스 옵션.
 */
import nationalities from '@/features/startTask/preset_task/my/components/shared/data/MBRS/nationalities.json';
import occupations from '@/features/startTask/preset_task/my/components/shared/data/MBRS/occupations.json';
import countries from '@/features/startTask/preset_task/my/components/shared/data/MBRS/countries.json';
import shareClassesJson from '@/features/startTask/preset_task/my/components/shared/data/idpro/share-classes.json';
import companySubtypesJson from '@/features/startTask/preset_task/my/components/shared/data/common/company-subtypes.json';
import racesJson from '@/features/startTask/preset_task/my/components/shared/data/common/races.json';
import idTypesJson from '@/features/startTask/preset_task/my/components/shared/data/idpro/id-types.json';

export interface ComboOpt { value: string; label: string; }

// ── 콤보 표시·매핑 케이스 (단일 토글) ────────────────────────────────
//   Nationality / Race / Occupation / Country 옵션 + resolver 출력에 공통 적용.
//   'upper' = 전부 대문자(기본). 여기만 'title' 로 바꾸면 옵션·값 전체가 Title Case 로 전환된다.
export type ComboCase = 'upper' | 'title';
export const COMBO_DISPLAY_CASE: ComboCase = 'upper';
export function applyComboCase(s: string, mode: ComboCase = COMBO_DISPLAY_CASE): string {
  const t = String(s ?? '').trim();
  if (!t) return '';
  return mode === 'upper'
    ? t.toUpperCase()
    : t.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase()); // Title Case (& / - 등 구분자 유지)
}
/** 옵션 리스트에 표시 케이스 적용 (value===label 로 통일). */
const caseOptions = (list: ComboOpt[]): ComboOpt[] =>
  list.map((o) => { const v = applyComboCase(o.value); return { value: v, label: v }; });

export type ComboSource =
  | 'nationality'
  | 'occupation'
  | 'country'
  | 'shareClass'
  | 'companyType'
  | 'purpose'
  | 'companySubType'
  | 'race'
  | 'idType';

export const NATIONALITIES = caseOptions(nationalities as ComboOpt[]);
export const OCCUPATIONS = caseOptions(occupations as ComboOpt[]);
export const COUNTRIES: ComboOpt[] = caseOptions(
  (countries as { value: string; label: string }[]).map((c) => {
    const label = String(c.label || '').trim();
    return { value: label, label };
  }),
);

export const SHARE_CLASSES: ComboOpt[] = (shareClassesJson as { value: string; label: string }[]).map((c) => {
  const label = String(c.label || c.value || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
  return { value: label, label };
});

/** Company Type — MY 설립 화면과 동일 (LIMITED BY SHARES 고정) */
export const COMPANY_TYPES: ComboOpt[] = [
  { value: 'LIMITED BY SHARES', label: 'LIMITED BY SHARES' },
];

/** Purpose — MY 설립 화면과 동일 (NEW INCORPORATION 고정) */
export const PURPOSES: ComboOpt[] = [
  { value: 'NEW INCORPORATION', label: 'NEW INCORPORATION' },
];

/** Sub Type — LIMITED BY SHARES 하위: SDN. BHD. / BERHAD */
export const COMPANY_SUBTYPES: ComboOpt[] = (
  (companySubtypesJson as Record<string, { value: string; label: string }[]>).LIMITED_BY_SHARES || []
).map((c) => ({
  value: String(c.label || c.value || '').trim(),
  label: String(c.label || c.value || '').trim(),
})).filter((c) => !!c.value);

/** Race — MY 설립/임원 화면과 동일 */
export const RACES: ComboOpt[] = caseOptions((racesJson as { value: string; label: string }[]).map((c) => ({
  value: String(c.value || '').trim(),
  label: String(c.label || c.value || '').trim(),
})).filter((c) => !!c.value));

/** ID Type — MY Identification Type 과 동일 (표시는 label) */
export const ID_TYPES: ComboOpt[] = (idTypesJson as { value: string; label: string }[]).map((c) => {
  const label = String(c.label || c.value || '').trim();
  return { value: label, label };
}).filter((c) => !!c.value);

export function comboOptions(src: ComboSource | null): ComboOpt[] {
  if (src === 'nationality') return NATIONALITIES;
  if (src === 'occupation') return OCCUPATIONS;
  if (src === 'country') return COUNTRIES;
  if (src === 'shareClass') return SHARE_CLASSES;
  if (src === 'companyType') return COMPANY_TYPES;
  if (src === 'purpose') return PURPOSES;
  if (src === 'companySubType') return COMPANY_SUBTYPES;
  if (src === 'race') return RACES;
  if (src === 'idType') return ID_TYPES;
  return [];
}

const NAT_CODE: Record<string, string> = { B15135: 'MALAYSIA' };

export function resolveNationality(raw: string): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const candidate = NAT_CODE[s.toUpperCase()] || s;
  const hit = NATIONALITIES.find((o) => o.value.toUpperCase() === candidate.toUpperCase());
  return hit ? hit.value : '';
}

export function resolveCountryOption(raw: string): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const cand = s.toUpperCase() === 'B15135' ? 'MALAYSIA' : s;
  const hit = COUNTRIES.find((o) => o.value.toUpperCase() === cand.toUpperCase());
  return hit ? hit.value : '';
}

export function resolveOccupation(raw: string): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const hit = OCCUPATIONS.find((o) => o.value.toUpperCase() === s.toUpperCase());
  return hit ? hit.value : applyComboCase(s); // 목록에 없어도 표시 케이스로 통일
}

export function resolveShareClass(raw: string): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const norm = s.replace(/_/g, ' ').replace(/\s+/g, ' ').toUpperCase();
  const hit = SHARE_CLASSES.find((o) => o.value.toUpperCase() === norm || o.label.toUpperCase() === norm);
  if (hit) return hit.value;
  return s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function codeToSpacedLabel(raw: string): string {
  return String(raw || '')
    .trim()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .toUpperCase();
}

/** NEW_INCORPORATION / "N - NEW INCORPORATION" → 콤보 value */
export function resolvePurpose(raw: string): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const norm = codeToSpacedLabel(s).replace(/^[NCF]\s*-\s*/, '');
  const hit = PURPOSES.find(
    (o) => o.value === norm || o.label === norm || norm.includes(o.value),
  );
  return hit ? hit.value : norm;
}

/** LIMITED_BY_SHARES / "Company limited by shares" → LIMITED BY SHARES */
export function resolveCompanyType(raw: string): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const norm = codeToSpacedLabel(s)
    .replace(/^COMPANY\s+/, '')
    .replace(/\s+COMPANY$/, '');
  // SDN. BHD. 등 suffix 가 type 으로 들어오면 무시
  if (COMPANY_SUBTYPES.some((o) => o.value.toUpperCase() === norm || o.label.toUpperCase() === norm)) {
    return '';
  }
  if (/SDN|BERHAD|SENDIRIAN/i.test(s) && !/LIMITED|UNLIMITED|GUARANTEE/i.test(s)) {
    return '';
  }
  const hit = COMPANY_TYPES.find(
    (o) =>
      o.value === norm ||
      o.label === norm ||
      norm.includes(o.value) ||
      o.value.includes(norm),
  );
  return hit ? hit.value : norm;
}

/** SDN_BHD / SDN. BHD. → SDN. BHD. */
export function resolveCompanySubType(raw: string): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const norm = codeToSpacedLabel(s).replace(/\./g, '');
  const hit = COMPANY_SUBTYPES.find((o) => {
    const ov = o.value.toUpperCase().replace(/\./g, '');
    const ol = o.label.toUpperCase().replace(/\./g, '');
    return ov === norm || ol === norm || ov.replace(/\s+/g, '') === norm.replace(/\s+/g, '');
  });
  return hit ? hit.value : s.replace(/_/g, ' ');
}

/** Malay / MALAY / OTHER RACE → 콤보 value */
export function resolveRace(raw: string): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const norm = codeToSpacedLabel(s);
  const hit = RACES.find((o) => {
    const ov = o.value.toUpperCase().replace(/_/g, ' ');
    const ol = o.label.toUpperCase().replace(/_/g, ' ');
    return ov === norm || ol === norm || o.value.toUpperCase() === s.toUpperCase();
  });
  if (hit) return hit.value;
  if (/other/i.test(s)) {
    const other = RACES.find((o) => /other/i.test(o.value) || /other/i.test(o.label));
    if (other) return other.value;
  }
  return '';
}

/** NRIC / PASSPORT / Passport → 콤보 label(value) */
export function resolveIdType(raw: string): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/pass/i.test(s)) {
    const hit = ID_TYPES.find((o) => /pass/i.test(o.value) || /pass/i.test(o.label));
    return hit ? hit.value : 'Passport';
  }
  if (/nric|mykad|my\s*kad|\bic\b/i.test(s)) {
    const hit = ID_TYPES.find((o) => /^nric$/i.test(o.value) || /^nric$/i.test(o.label));
    return hit ? hit.value : 'NRIC';
  }
  const norm = codeToSpacedLabel(s);
  const hit = ID_TYPES.find((o) => {
    const ov = o.value.toUpperCase().replace(/_/g, ' ');
    const ol = o.label.toUpperCase().replace(/_/g, ' ');
    return ov === norm || ol === norm;
  });
  return hit ? hit.value : '';
}
